// The signed-in console's frame (spec 15 §4; D-626): a left rail of screens
// grouped by job, a top bar with the deployment badge, the signed-in staff
// member and sign-out, and the screen in the remaining width.
//
// It is the console's own shell, built from the design system's tokens and
// primitives (styles/*, components/*) rather than from the office AppShell,
// because the console has no tenant, no global search and no module list,
// and its rail is grouped where the office's is flat. Desktop-first; from
// 720 px down the rail becomes a drawer behind a Menu button, so the console
// stays usable on a tablet and a phone without sideways scrolling.
//
// The shell owns sign-out (one control, in the top bar, on every screen); no
// screen draws its own.

import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/button";
import {
  IconAudit,
  IconBars,
  IconBuilding,
  IconHome,
  IconLifebuoy,
  IconMenu,
  IconPeople,
  IconPerson,
  IconPulse,
  IconSignOut,
} from "../../../components/icons";
import { LocaleSwitcher } from "../../../components/locale-switcher";
import { EmptyState } from "../../../components/states";
import { StatusChip } from "../../../components/status-chip";
import { Banner, Toasts } from "../../../components/toast";
import { useForgetPlatformSession } from "../hooks/use-platform-session";
import { platformLogout, type PlatformMe } from "../lib/platform-api";
import {
  consoleEnvironment,
  consoleNavFor,
  navEntryLit,
  type ConsoleEnvironment,
  type ConsoleNavIcon,
} from "./console-nav";

const ICONS: Record<ConsoleNavIcon, () => ReactNode> = {
  overview: IconHome,
  tenants: IconBuilding,
  support: IconLifebuoy,
  health: IconPulse,
  usage: IconBars,
  staff: IconPeople,
  audit: IconAudit,
  account: IconPerson,
};

const ENV_VARIANT: Record<ConsoleEnvironment, "bad" | "warn" | "neutral"> = {
  // Production is the one a slip costs real customers, so it is the loud one.
  production: "bad",
  staging: "warn",
  local: "neutral",
};

/** The role, translated when the catalogue knows it, else the raw code: the
 * API and the bundle deploy apart, so a new role can arrive first. */
export function useRoleLabel(role: string): string {
  const { t, i18n } = useTranslation();
  const key = `admin.roles.${role}`;
  return i18n.exists(key) ? t(key) : role;
}

function ConsoleRail({ me, onNavigate }: { me: PlatformMe; onNavigate?: () => void }) {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return (
    <nav className="admin-rail-nav" aria-label={t("admin.nav.label")} data-testid="admin-nav">
      {consoleNavFor(me).map(({ group, entries }) => (
        <div key={group} className="admin-rail-group" data-group={group}>
          <p className="admin-rail-group-t">{t(`admin.navGroups.${group}`)}</p>
          {entries.map((entry) => {
            const Icon = ICONS[entry.icon];
            const lit = navEntryLit(entry, pathname);
            return (
              <Link
                key={entry.key}
                to={entry.to}
                className={lit ? "admin-rail-link is-lit" : "admin-rail-link"}
                aria-current={lit ? "page" : undefined}
                data-testid={`admin-nav-${entry.key}`}
                onClick={onNavigate}
              >
                <Icon />
                <span className="admin-rail-label">{t(entry.labelKey)}</span>
              </Link>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

function Brand() {
  const { t } = useTranslation();
  return (
    <div className="admin-rail-brand">
      <span className="admin-brand-mk" aria-hidden="true">
        A
      </span>
      <span className="admin-rail-brand-text">
        <span className="admin-brand-name">{t("admin.brand")}</span>
        <span className="admin-rail-brand-sub">{t("admin.console")}</span>
      </span>
    </div>
  );
}

export function ConsoleShell({ me, children }: { me: PlatformMe; children: ReactNode }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const forgetSession = useForgetPlatformSession();
  const roleLabel = useRoleLabel(me.role);
  const env = consoleEnvironment(window.location.hostname);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawerId = useId();
  const menuButton = useRef<HTMLButtonElement | null>(null);
  const drawer = useRef<HTMLDivElement | null>(null);

  // A route change closes the drawer: a link inside it, Back, or a screen's
  // own navigation all leave it showing the wrong place.
  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  // While the drawer is open the rail and the page behind it are `inert`, so
  // Tab, a screen reader's virtual cursor and a stray click stay inside the
  // dialog its `aria-modal` promises (D-629 ruling 15). Escape gives focus back
  // to Menu, but only once the page is no longer inert: a focus() on an inert
  // control does nothing, so it waits for the commit that lifts it.
  const returnFocus = useRef(false);
  useEffect(() => {
    if (!drawerOpen) {
      if (returnFocus.current) {
        returnFocus.current = false;
        menuButton.current?.focus();
      }
      return undefined;
    }
    drawer.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        returnFocus.current = true;
        setDrawerOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [drawerOpen]);

  const signOut = useMutation({
    mutationFn: platformLogout,
    // Whatever the server said, the browser leaves: a sign-out that stranded
    // the operator on a signed-in screen because the network hiccuped would be
    // worse than a server session left to its own 12-hour ceiling.
    onSettled: () => {
      forgetSession();
      void navigate({ to: "/login" });
    },
  });

  return (
    <div className="admin-console" data-testid="admin-console" data-env={env}>
      <aside className="admin-rail" data-testid="admin-rail" inert={drawerOpen}>
        <Brand />
        <ConsoleRail me={me} />
      </aside>

      <div className="admin-console-main" inert={drawerOpen}>
        <header className="admin-console-top" data-testid="admin-topbar">
          <button
            type="button"
            ref={menuButton}
            className="ds-btn admin-menu-btn"
            aria-expanded={drawerOpen}
            aria-controls={drawerOpen ? drawerId : undefined}
            data-testid="admin-menu"
            onClick={() => {
              setDrawerOpen((open) => !open);
            }}
          >
            <IconMenu />
            <span>{t("admin.shell.menu")}</span>
          </button>
          <StatusChip variant={ENV_VARIANT[env]} data-testid="admin-env">
            {t(`admin.shell.env.${env}`)}
          </StatusChip>
          <div className="admin-console-top-right">
            <LocaleSwitcher className="ds-select" />
            <span className="admin-whoami" data-testid="admin-whoami">
              <span className="admin-whoami-email" data-testid="admin-console-email">
                {me.email}
              </span>
              <span className="admin-whoami-role" data-testid="admin-console-role">
                {roleLabel}
                {me.scope === "sandbox" ? ` · ${t("admin.shell.sandboxScope")}` : ""}
              </span>
            </span>
            <Button
              size="sm"
              busy={signOut.isPending}
              busyLabel={t("admin.home.signingOut")}
              data-testid="admin-signout"
              onClick={() => {
                signOut.mutate();
              }}
            >
              <IconSignOut />
              <span>{t("admin.home.signOut")}</span>
            </Button>
          </div>
        </header>

        {signOut.error !== null ? (
          <Banner kind="bad" data-testid="admin-signout-error">
            {t("admin.home.signOutFailed")}
          </Banner>
        ) : null}

        <main className="admin-console-content">
          <div className="admin-page">{children}</div>
        </main>
      </div>

      {drawerOpen ? (
        <>
          <div
            className="admin-drawer-scrim"
            aria-hidden="true"
            onClick={() => {
              setDrawerOpen(false);
            }}
          />
          <div
            ref={drawer}
            id={drawerId}
            role="dialog"
            aria-modal="true"
            aria-label={t("admin.nav.label")}
            tabIndex={-1}
            className="admin-drawer"
            data-testid="admin-drawer"
          >
            <Brand />
            <ConsoleRail
              me={me}
              onNavigate={() => {
                setDrawerOpen(false);
              }}
            />
          </div>
        </>
      ) : null}
      <Toasts />
    </div>
  );
}

/** A console path no screen answers: inside the shell, with the way home. */
export function ConsoleNotFound() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <section className="admin-screen" data-testid="admin-console-not-found">
      <EmptyState
        title={t("admin.notFound.title")}
        body={t("admin.shell.notFoundBody")}
        ctaLabel={t("admin.shell.goHome")}
        onCta={() => {
          void navigate({ to: "/console" });
        }}
      />
    </section>
  );
}
